'use client';
import type { PropsWithChildren } from 'react';
import type { FeatureFlags } from 'api/getFeatureFlags';
import { ConsentProvider } from 'components/lib/Sourcepoint';
import { AppProvider, type AnalyticsDataProps } from 'context/AppContext';
import type { AdSizePredictionProps } from 'types/ads';
import type { StructureTypes } from 'types/cms';
import type { TickarooPrefetchData } from 'types/storylines';
import { GeoContextProvider } from './GeoContext';
import { TrackingProvider } from './TrackingContext';

interface PageMetaDataProps {
  adZone: string;
  noAds: boolean;
  isStoryline: boolean;
  noVideoAds?: boolean;
  keywords?: Array<string>;
  adTags?: Array<string>;
}

export type ProvidersProps = PropsWithChildren<{
  readonly pageType?: StructureTypes;
  readonly pageTitle?: string;
  readonly adSizePrediction?: AdSizePredictionProps;
  readonly prefetchedData?: TickarooPrefetchData;
  readonly requestPath?: string;
  readonly featureFlags?: FeatureFlags;
  readonly env?: string;
  readonly version?: string;
  readonly device?: string;
  readonly os?: string;
  readonly trafficSource?: string;
  readonly sectionLogoColors?: [string, string, string];
  readonly pageMetaData?: PageMetaDataProps;
  readonly analyticsData?: AnalyticsDataProps;
}>;

export function Providers({
  children,
  pageType,
  pageTitle,
  adSizePrediction,
  prefetchedData,
  requestPath,
  featureFlags,
  env,
  version,
  device,
  os,
  trafficSource,
  sectionLogoColors,
  pageMetaData,
  analyticsData,
}: ProvidersProps) {
  return (
    <AppProvider
      value={{
        pageType,
        pageTitle,
        adSizePrediction,
        prefetchedData,
        requestPath,
        featureFlags,
        env,
        version,
        device,
        os,
        trafficSource,
        sectionLogoColors,
        pageMetaData,
        analyticsData,
      }}
    >
      <TrackingProvider>
        <GeoContextProvider>
          <ConsentProvider>{children}</ConsentProvider>
        </GeoContextProvider>
      </TrackingProvider>
    </AppProvider>
  );
}
